import * as React from 'react'
import { SubscriptionCode, SubscriptionDetails } from '@modules/subscription/models'
import Table from '@components/table/table'
import { parseISODate, toDefaultDateFormat } from '@helpers/date-helper'
import { SubscriptionDetailsCodesRowActions } from '@modules/subscription/details/code-rows-actions'
import { SubscriptionDetailsCodesRowCode } from '@modules/subscription/details/code-rows-code'
import { YesNoBadge } from '@components/badges/yes-no'
import { useAuthenticatedUser } from '@components/hooks/use-authenticated-user'
import { useSubscriptionTypeStrategyProvider } from '@modules/subscription/hooks/use-subscription-type-provider'
import { SubscriptionCodeConversionRevert } from '@modules/subscription/details/code-convert/code-conversion-revert'
import { SubscriptionDetailsCodesRowRenewAction } from '@modules/subscription/details/code-rows-renew-action'

interface Props {
  subscriptionCode: SubscriptionCode
  index: number
  readOnly?: boolean
  children: React.ReactNode
  subscriptionDetails: SubscriptionDetails
}

export const SubscriptionDetailsCodesRow: React.FC<Props> = ({
  children,
  subscriptionCode,
  index,
  subscriptionDetails,
  readOnly = false,
}) => {
  const user = useAuthenticatedUser()
  const { crud_permissions } = useSubscriptionTypeStrategyProvider(subscriptionDetails.type)
  const { crud_permissions: renewPermissions } = useSubscriptionTypeStrategyProvider(
    subscriptionDetails.is_winter_package ? 'DEFAULT' : subscriptionDetails.type,
  )

  const createdYear = parseISODate(subscriptionDetails.created)?.getFullYear() || 0

  const canRenewReadOnly = subscriptionDetails.is_winter_package || subscriptionDetails.type === 'PRODUCT_PACKAGE'
  const hasRenewPermission = user.hasPerm(renewPermissions.renew)

  return (
    <Table.Row>
      {!subscriptionCode.is_used &&
      ['localization', 'default'].includes(subscriptionCode.kind) &&
      !subscriptionCode.is_splitted ? (
        children
      ) : (
        <Table.Cell />
      )}
      <Table.Cell style={{ width: 5 }}>
        <a href={subscriptionCode.urls.admin} target="_blank">
          {index}.
        </a>
      </Table.Cell>
      <SubscriptionDetailsCodesRowCode
        subscriptionDetails={subscriptionDetails}
        createdYear={createdYear}
        subscriptionCode={subscriptionCode}
      />
      <Table.Cell>
        <YesNoBadge
          value={subscriptionCode.is_splitted}
          textYes={`tak (${subscriptionCode.splitted_used} z ${subscriptionCode.splitted_available})`}
        />
      </Table.Cell>
      <Table.Cell>
        <YesNoBadge value={subscriptionCode.is_used} />
      </Table.Cell>
      <Table.Cell>
        {subscriptionCode.localization_kind_display}
        <SubscriptionCodeConversionRevert
          subscriptionCode={subscriptionCode}
          subscriptionDetails={subscriptionDetails}
          kind="localization"
        />
      </Table.Cell>
      <Table.Cell>{subscriptionCode.kind_display}</Table.Cell>
      <Table.Cell>{toDefaultDateFormat(subscriptionCode.expire_after)}</Table.Cell>

      {readOnly ? (
        <Table.Cell>
          {canRenewReadOnly && (
            <SubscriptionDetailsCodesRowRenewAction subscriptionCode={subscriptionCode} canRenew={hasRenewPermission} />
          )}
        </Table.Cell>
      ) : (
        <SubscriptionDetailsCodesRowActions
          canUpdate={user.hasPerm(crud_permissions.update)}
          canRenew={hasRenewPermission}
          subscriptionCode={subscriptionCode}
        />
      )}
    </Table.Row>
  )
}
